<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePostHog } from '@/composables/usePostHog.js'
import MdiChartBar from '~icons/mdi/chart-bar'
import MdiShieldCheck from '~icons/mdi/shield-check'
import MdiVideoOutline from '~icons/mdi/video-outline'
import MdiInformationOutline from '~icons/mdi/information-outline'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronUp from '~icons/mdi/chevron-up'

defineProps({ shake: { type: Boolean, default: false } })

const { giveConsent, revokeConsent } = usePostHog()
const { t } = useI18n()
const expanded = ref(false)
</script>

<template>
  <div
    role="dialog"
    aria-labelledby="consent-title"
    :class="['mt-6 rounded-lg p-4 text-sm', shake ? 'shake' : '']"
    style="background: var(--color-surface-2); border: 1px solid var(--color-border);"
  >
    <!-- Header -->
    <div class="flex items-center gap-2 mb-2">
      <MdiChartBar class="w-4 h-4 shrink-0" style="color: var(--color-accent)" />
      <p id="consent-title" class="font-semibold text-base" style="color: var(--color-text)">
        {{ $t('analytics.title') }}
      </p>
    </div>

    <!-- Summary -->
    <p class="leading-relaxed" style="color: var(--color-muted)">
      {{ $t('analytics.summary') }}
      <strong style="color: var(--color-text)">{{ $t('analytics.appreciate') }}</strong>
    </p>

    <!-- Toggle -->
    <button
      type="button"
      class="mt-2 flex items-center gap-1 text-xs font-medium"
      style="color: var(--color-accent); background: none; border: none; padding: 0; cursor: pointer;"
      @click="expanded = !expanded"
    >
      <component :is="expanded ? MdiChevronUp : MdiChevronDown" class="w-3.5 h-3.5" />
      {{ expanded ? $t('analytics.hideDetails') : $t('analytics.whatCollected') }}
    </button>

    <!-- Expandable detail -->
    <div v-if="expanded" class="mt-3 flex flex-col gap-3">

      <!-- What is collected -->
      <div class="flex flex-col gap-2" style="color: var(--color-muted)">
        <div class="flex items-start gap-2">
          <MdiChartBar class="w-3.5 h-3.5 mt-0.5 shrink-0" style="color: var(--color-accent)" />
          <span>
            <strong style="color: var(--color-text)">{{ $t('analytics.usageStats') }}</strong> —
            {{ $t('analytics.usageStatsDesc') }}
          </span>
        </div>
        <div class="flex items-start gap-2">
          <MdiVideoOutline class="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
          <span>
            <strong style="color: var(--color-text)">{{ $t('analytics.sessionReplays') }}</strong> —
            {{ $t('analytics.sessionReplaysDesc') }}
            <strong style="color: var(--color-text)">{{ $t('analytics.mayCapture') }}</strong>
            {{ $t('analytics.exampleContent') }}
          </span>
        </div>
      </div>

      <!-- Privacy guarantees -->
      <div class="flex flex-col gap-1" style="color: var(--color-muted)">
        <span class="flex items-center gap-1.5">
          <MdiShieldCheck class="w-3.5 h-3.5 shrink-0 text-emerald-400" />
          {{ $t('analytics.gdpr') }}
        </span>
        <span class="flex items-center gap-1.5">
          <MdiShieldCheck class="w-3.5 h-3.5 shrink-0 text-emerald-400" />
          {{ $t('analytics.noIp') }}
        </span>
        <span class="flex items-center gap-1.5">
          <MdiShieldCheck class="w-3.5 h-3.5 shrink-0 text-emerald-400" />
          {{ $t('analytics.staffOnly') }}
        </span>
      </div>

      <!-- Development note + ad blocker -->
      <div class="flex flex-col gap-1.5 text-xs leading-relaxed" style="color: var(--color-muted)">
        <p class="flex items-start gap-1.5">
          <MdiInformationOutline class="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
          {{ $t('analytics.devPhase') }}
        </p>
        <p class="flex items-start gap-1.5">
          <MdiInformationOutline class="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
          {{ $t('analytics.adBlocker') }}
        </p>
      </div>
    </div>

    <!-- Actions -->
    <div class="mt-4 flex flex-wrap gap-2 items-center">
      <button class="btn primary small" @click="giveConsent">{{ $t('analytics.accept') }}</button>
      <button class="btn small" @click="revokeConsent">{{ $t('analytics.decline') }}</button>
    </div>

    <!-- No-thanks clarification -->
    <p class="mt-2 flex items-start gap-1.5 text-xs leading-snug" style="color: var(--color-muted)">
      <MdiShieldCheck class="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
      <em>{{ $t('analytics.noThanksNote') }}</em>
    </p>
  </div>
</template>
